import { useState, useRef, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toTitleCase } from "@/lib/utils";
import {
  Camera, Keyboard, X, Trash2, Upload, Loader2, FileCheck, Clock,
  CheckCircle, XCircle, QrCode, Plus, ShieldCheck,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function QRScanner() {
  const { refreshUser } = useAuth();
  const [session, setSession] = useState(null);      // draft session {id, items[], invoice_url}
  const [mySessions, setMySessions] = useState([]);   // submitted history
  const [mode, setMode] = useState("list");           // list | camera | manual | submitted
  const [manualQR, setManualQR] = useState("");
  const [scanning, setScanning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitResult, setSubmitResult] = useState(null);
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  // Point 35 — capture the customer's live location to send with each scan (geofence).
  const geoRef = useRef({ lat: null, lng: null });
  const getPosition = () => new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => { const c = { lat: pos.coords.latitude, lng: pos.coords.longitude }; geoRef.current = c; resolve(c); },
      () => resolve(geoRef.current || { lat: null, lng: null }),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  });

  const items = session?.items || [];
  const estimatedPoints = items.reduce((s, it) => s + (Number(it.points_earned) || 0), 0);
  const hasInvoice = !!session?.invoice_url;
  const canSubmit = items.length > 0 && hasInvoice;

  const loadSession = useCallback(async () => {
    try {
      const { data } = await API.get("/qr/session/current");
      setSession(data || null);
    } catch { /* ignore */ }
  }, []);

  const loadMySessions = useCallback(async () => {
    try {
      const { data } = await API.get("/qr/sessions");
      setMySessions(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadSession();
    loadMySessions();
    getPosition();
    return () => { if (qrScannerRef.current) { try { qrScannerRef.current.stop(); qrScannerRef.current.destroy(); } catch { /* ignore */ } qrScannerRef.current = null; } };
  }, [loadSession, loadMySessions]);

  // ── Camera ────────────────────────────────────────────────────────
  const failToManual = (name) => {
    qrScannerRef.current = null;
    let msg = "Camera unavailable. Enter the QR code manually below.";
    if (name === "NotAllowedError" || name === "PermissionDeniedError") msg = "Camera permission denied. Enter the QR code manually below.";
    else if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") msg = "No camera found on this device. Enter the QR code manually below.";
    setError(msg);
    setMode("manual");
  };

  const startCamera = async () => {
    setError(""); setNotice("");
    setMode("camera");
    try {
      // Point 52: switch to the worker-based `qr-scanner` engine for instant,
      // Paytm/GPay-style detection. It decodes QR frames in a Web Worker (many
      // scans/sec, non-blocking), auto-uses the device's native BarcodeDetector
      // when available, and manages the camera in an iOS-safe way (no manual
      // resolution/aspectRatio constraints that previously crashed iOS — P50).
      const QrScanner = (await import("qr-scanner")).default;
      setTimeout(async () => {
        if (!videoRef.current) { failToManual(); return; }
        const hasCam = await QrScanner.hasCamera().catch(() => false);
        if (!hasCam) { failToManual("NotFoundError"); return; }
        const onDecode = (result) => {
          const text = (result && typeof result === "object") ? result.data : result;
          if (!text) return;
          const s = qrScannerRef.current;
          qrScannerRef.current = null;
          if (s) { try { s.stop(); s.destroy(); } catch { /* ignore */ } }
          addScan(text);
        };
        const scanner = new QrScanner(videoRef.current, onDecode, {
          preferredCamera: "environment",
          highlightScanRegion: false,
          highlightCodeOutline: true,
          maxScansPerSecond: 25,
          returnDetailedScanResult: true,
          // GPay-style: scan the WHOLE frame (not a small centre crop) so the QR is
          // detected wherever it sits in view and without precise aligning.
          calculateScanRegion: (video) => {
            const w = video.videoWidth || video.clientWidth || 640;
            const h = video.videoHeight || video.clientHeight || 480;
            const scale = Math.min(1, 1000 / Math.max(w, h));
            return { x: 0, y: 0, width: w, height: h, downScaledWidth: Math.round(w * scale), downScaledHeight: Math.round(h * scale) };
          },
        });
        qrScannerRef.current = scanner;
        try {
          await scanner.start();
          // Best-effort sharpen: higher resolution + continuous autofocus make small
          // reagent QRs decode instantly. `advanced` constraints are OPTIONAL hints,
          // so they never throw OverconstrainedError (unlike camera-open constraints).
          try {
            const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
            if (track?.applyConstraints) {
              await track.applyConstraints({ advanced: [{ width: 1920 }, { height: 1080 }, { focusMode: "continuous" }] });
            }
          } catch { /* ignore — best effort */ }
        } catch (e) {
          try { scanner.destroy(); } catch { /* ignore */ }
          qrScannerRef.current = null;
          failToManual(e?.name);
        }
      }, 150);
    } catch (err) { failToManual(err?.name); }
  };

  const stopCamera = () => {
    if (qrScannerRef.current) { try { qrScannerRef.current.stop(); qrScannerRef.current.destroy(); } catch { /* ignore */ } qrScannerRef.current = null; }
    setMode("list");
  };

  // ── Session actions ─────────────────────────────────────────────────
  const addScan = async (qrString) => {
    const qs = (qrString || "").trim();
    if (!qs) return;
    setScanning(true); setError(""); setNotice("");
    setMode("list");
    try {
      const pos = await getPosition();
      const { data } = await API.post("/qr/session/scan", { session_id: session?.id, qr_string: qs, latitude: pos.lat, longitude: pos.lng });
      setSession(data.session);
      const last = (data.session?.items || []).slice(-1)[0];
      setNotice(`${toTitleCase(last?.product_name || "Reagent")} added${last?.points_earned ? ` · +${last.points_earned} pts (pending)` : ""}`);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setScanning(false);
      setManualQR("");
    }
  };

  const removeItem = async (qrString) => {
    setError(""); setNotice("");
    try {
      const { data } = await API.delete(`/qr/session/item?qr_string=${encodeURIComponent(qrString)}`);
      setSession(data.session);
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
  };

  const uploadInvoice = async (file) => {
    if (!file) return;
    if (items.length === 0) { setError("Scan at least one QR code before uploading the invoice."); return; }
    setUploading(true); setError(""); setNotice("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await API.post("/qr/session/invoice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadSession();
      setNotice("Invoice attached.");
    } catch (err) { setError(formatError(err.response?.data?.detail) || "Invoice upload failed"); }
    finally { setUploading(false); }
  };

  const submitSession = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError("");
    try {
      const { data } = await API.post("/qr/session/submit");
      setSubmitResult(data);
      setSession(null);
      setMode("submitted");
      loadMySessions();
      refreshUser();
    } catch (err) { setError(formatError(err.response?.data?.detail) || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  const submitManual = (e) => { e.preventDefault(); if (manualQR.trim()) addScan(manualQR.trim()); };

  const startNewBatch = () => { setSubmitResult(null); setMode("list"); loadSession(); };

  const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  const statusChip = (s) => {
    if (s === "approved") return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3" />Approved</span>;
    if (s === "rejected") return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700"><XCircle className="w-3 h-3" />Rejected</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><Clock className="w-3 h-3" />Pending review</span>;
  };

  // ── Submitted confirmation screen ───────────────────────────────────
  if (mode === "submitted" && submitResult) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-slide-up" data-testid="session-submitted">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-20 h-20 rounded-2xl mx-auto mb-5 flex items-center justify-center bg-amber-50 border border-amber-100">
            <ShieldCheck className="w-10 h-10 text-amber-500" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-1 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Submitted for Approval</h2>
          <p className="text-gray-500 text-sm mb-6">
            {submitResult.item_count} QR code{submitResult.item_count === 1 ? "" : "s"} and your invoice have been sent to the TransAsia team.
          </p>
          <div className="bg-gradient-to-br from-gray-900 to-[#008A99] rounded-2xl p-6 text-white mb-6 shadow-xl">
            <p className="text-xs font-medium text-white/50 uppercase tracking-wider mb-1">Points pending approval</p>
            <p className="text-5xl font-black" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }} data-testid="session-estimated-points">{submitResult.estimated_points}</p>
            <p className="text-xs text-white/50 uppercase tracking-wider mt-1">Points</p>
          </div>
          <p className="text-xs text-gray-500 mb-5">
            Points will be credited to your balance once an admin verifies your invoice. You can track the status below.
          </p>
          <Button onClick={startNewBatch} className="w-full h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-semibold shadow-lg shadow-[#008A99]/20 transition-all" data-testid="scan-new-batch-button">
            Scan a New Batch
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up pb-8" data-testid="qr-scanner">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Scan QR Codes</h2>
        <p className="text-sm text-gray-500 mt-1.5">Scan all reagent QRs, attach the invoice, and submit for approval.</p>
      </div>

      {error && (
        <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center font-medium" data-testid="scan-error">
          {error}
          <button onClick={() => setError("")} className="ml-2 underline font-semibold">Dismiss</button>
        </div>
      )}
      {notice && !error && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm text-center font-medium" data-testid="scan-notice">{notice}</div>
      )}

      {/* Camera */}
      {mode === "camera" && (
        <div className="max-w-md mx-auto">
          <div className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-xl" style={{ minHeight: 320 }}>
            <video ref={videoRef} data-testid="qr-video" playsInline muted className="w-full h-full object-cover" style={{ minHeight: 320 }} />
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#008A99] animate-scan-line opacity-80 pointer-events-none" />
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">Point the camera at the QR code — it detects automatically.</p>
          <Button onClick={stopCamera} variant="outline" className="w-full mt-3 rounded-xl border-gray-200 font-semibold" data-testid="cancel-camera-button">
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
        </div>
      )}

      {/* Manual entry */}
      {mode === "manual" && (
        <form onSubmit={submitManual} className="max-w-md mx-auto space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-[0.15em] mb-2 block">QR Code String</label>
            <Input data-testid="manual-qr-input" value={manualQR} onChange={(e) => setManualQR(e.target.value)}
              placeholder="e.g. 121740*B012273 : 00071*01/2022*01/2024"
              className="h-12 rounded-xl border-gray-200 focus:border-[#008A99] focus:ring-[#008A99]" />
            <p className="text-xs text-gray-400 mt-2">Enter the QR string printed on your reagent kit</p>
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => { setMode("list"); setError(""); }} className="flex-1 h-12 rounded-xl border-gray-200 font-semibold" data-testid="back-button">Back</Button>
            <Button type="submit" disabled={scanning || !manualQR.trim()} className="flex-1 h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-semibold shadow-lg shadow-[#008A99]/20 disabled:shadow-none transition-all" data-testid="submit-qr-button">
              {scanning ? "Adding..." : "Add QR"}
            </Button>
          </div>
        </form>
      )}

      {/* List / batch builder */}
      {mode === "list" && (
        <div className="max-w-md mx-auto space-y-5">
          {scanning && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-2" data-testid="scan-processing">
              <Loader2 className="w-4 h-4 animate-spin text-[#008A99]" /> Verifying QR…
            </div>
          )}

          {/* Scanned items */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <QrCode className="w-4 h-4 text-[#008A99]" />
                <span className="text-sm font-bold text-gray-800">Scanned reagents</span>
              </div>
              <span className="text-xs font-semibold text-gray-400" data-testid="scan-item-count">{items.length} item{items.length === 1 ? "" : "s"}</span>
            </div>

            {items.length === 0 ? (
              <div className="text-center py-6" data-testid="scan-empty">
                <div className="w-14 h-14 bg-[#E0F4F5] rounded-2xl mx-auto flex items-center justify-center mb-2">
                  <QrCode className="w-7 h-7 text-[#008A99]" />
                </div>
                <p className="text-sm text-gray-400">No QR scanned yet. Tap below to start.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((it) => (
                  <div key={it.qr_string} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100" data-testid={`scan-item-${it.product_code}`}>
                    <div className="w-9 h-9 rounded-lg bg-[#E0F4F5] flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#008A99]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{toTitleCase(it.product_name || it.product_code)}</p>
                      <p className="text-[11px] text-gray-400 truncate font-mono">{it.qr_string}</p>
                    </div>
                    <span className="text-xs font-bold text-[#008A99] flex-shrink-0">+{it.points_earned || 0}</span>
                    <button onClick={() => removeItem(it.qr_string)} className="text-red-400 hover:text-red-600 p-1 flex-shrink-0" data-testid={`remove-scan-${it.product_code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t mt-1">
                  <span className="text-xs font-semibold text-gray-500">Estimated points (pending approval)</span>
                  <span className="text-sm font-black text-[#008A99]" data-testid="scan-estimated-points">+{estimatedPoints}</span>
                </div>
              </div>
            )}
          </div>

          {/* Scan actions */}
          <div className="grid grid-cols-2 gap-3">
            <button onClick={startCamera} className="bg-[#008A99] hover:bg-[#006670] text-white rounded-2xl p-4 flex flex-col items-center gap-2 transition-all shadow-lg shadow-[#008A99]/20" data-testid="camera-scan-button">
              <Camera className="w-6 h-6" />
              <span className="text-sm font-semibold">{items.length ? "Scan another" : "Camera Scan"}</span>
            </button>
            <button onClick={() => { setError(""); setMode("manual"); }} className="bg-white border-2 border-gray-200 hover:border-[#008A99] text-gray-700 rounded-2xl p-4 flex flex-col items-center gap-2 transition-all" data-testid="manual-entry-button">
              <Keyboard className="w-6 h-6 text-[#008A99]" />
              <span className="text-sm font-semibold">Enter manually</span>
            </button>
          </div>

          {/* Invoice upload */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileCheck className="w-4 h-4 text-[#008A99]" />
              <span className="text-sm font-bold text-gray-800">Invoice copy</span>
              <span className="text-[10px] font-bold text-red-500">*required</span>
            </div>
            {hasInvoice ? (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="invoice-attached">
                <img src={`${BACKEND}${session.invoice_url}`} alt="invoice" className="w-12 h-12 rounded-lg object-cover border border-emerald-200" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-700">Invoice attached</p>
                  <p className="text-[11px] text-emerald-600">{session.invoice_filename || "Uploaded"}</p>
                </div>
                <label className="text-xs font-semibold text-[#008A99] cursor-pointer underline">
                  Replace
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (f) uploadInvoice(f); e.target.value = ""; }} data-testid="invoice-replace-input" />
                </label>
              </div>
            ) : (
              <label className="flex items-center gap-3 px-4 py-3 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#008A99] hover:bg-[#E0F4F5]/30 transition-colors" data-testid="invoice-upload-label">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#008A99]" /> : <Upload className="w-5 h-5 text-gray-400" />}
                <span className="text-sm text-gray-500">{uploading ? "Uploading…" : "Upload invoice (PDF / JPG / PNG)"}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" disabled={items.length === 0 || uploading} onChange={(e) => { const f = e.target.files[0]; if (f) uploadInvoice(f); e.target.value = ""; }} data-testid="invoice-upload-input" />
              </label>
            )}
          </div>

          {/* Submit */}
          <Button onClick={submitSession} disabled={!canSubmit || submitting} className="w-full h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-bold shadow-lg shadow-[#008A99]/20 disabled:opacity-50 disabled:shadow-none transition-all" data-testid="submit-session-button">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {submitting ? "Submitting…" : "Submit for Approval"}
          </Button>
          {!canSubmit && (
            <p className="text-center text-xs text-gray-400" data-testid="submit-hint">
              {items.length === 0 ? "Scan at least one QR code." : "Attach the invoice copy to enable submit."}
            </p>
          )}

          {/* My submissions */}
          {mySessions.length > 0 && (
            <div className="pt-2" data-testid="my-sessions">
              <p className="text-sm font-bold text-gray-800 mb-2">My submissions</p>
              <div className="space-y-2">
                {mySessions.map((s) => (
                  <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center justify-between" data-testid={`my-session-${s.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{(s.items || []).length} reagent{(s.items || []).length === 1 ? "" : "s"} · {s.estimated_points || 0} pts</p>
                      <p className="text-[11px] text-gray-400">{fmtDate(s.submitted_at)}</p>
                      {s.status === "rejected" && s.admin_notes && <p className="text-[11px] text-red-500 mt-0.5">Reason: {s.admin_notes}</p>}
                    </div>
                    {statusChip(s.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
